import { memo, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import TerminalIcon from './icons/terminal.svg?react';
import FileIcon from './icons/file.svg?react';
import FunctionIcon from './icons/function.svg?react';
import CrossIcon from './icons/cross.svg?react';
import { IconButton } from './primitives/IconButton';

const TOOL_LABELS = {
  bash: 'shell commands',
  edit_file: 'file edits',
  create_file: 'file creation',
};

/**
 * ToolApprovalPopup - Inline approval UI for tool execution during agent loops
 *
 * Displays predictably above the chat input area (not as a modal) with tool-specific
 * icons, command preview, and approve/deny actions in a vertical list.
 *
 * Hotkeys: y = Allow, a = Always Allow, n / Esc = Deny, Arrow keys to navigate, Enter to confirm
 *
 * @component
 */
const ToolApprovalPopup = memo(({ tool, title, command, onAllow, onAlwaysAllow, onDeny }) => {
  const actions = [onAllow, onAlwaysAllow, onDeny];
  const [focusedIndex, setFocusedIndex] = useState(1);
  const buttonRefs = useRef([]);

  // Focus the active button when focusedIndex changes
  useEffect(() => {
    buttonRefs.current[focusedIndex]?.focus();
  }, [focusedIndex]);

  // Global hotkeys while popup is visible
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

      switch (e.key) {
        case 'ArrowDown':
        case 'j':
          e.preventDefault();
          setFocusedIndex(i => (i + 1) % 3);
          break;
        case 'ArrowUp':
        case 'k':
          e.preventDefault();
          setFocusedIndex(i => (i - 1 + 3) % 3);
          break;
        case 'Enter':
          e.preventDefault();
          actions[focusedIndex]();
          break;
        case 'y':
          e.preventDefault();
          onAllow();
          break;
        case 'a':
          e.preventDefault();
          onAlwaysAllow();
          break;
        case 'Escape':
          e.preventDefault();
          onDeny();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [focusedIndex, onAllow, onAlwaysAllow, onDeny, actions]);

  const getToolIcon = () => {
    if (tool === 'bash') {
      return <TerminalIcon className="size-3.5 shrink-0" />;
    }
    if (tool.includes('file') || tool === 'edit_file' || tool === 'create_file') {
      return <FileIcon className="size-3.5 shrink-0" />;
    }
    return <FunctionIcon className="size-3.5 shrink-0" />;
  };

  const toolLabel = TOOL_LABELS[tool] || tool;

  const options = [
    { key: 'y', label: <span>Yes, allow</span> },
    { key: 'a', label: <span>Yes, always allow <span className="text-tertiary">{toolLabel}</span></span> },
    { key: 'esc', label: <span>No, deny</span> },
  ];

  return (
    <div className="relative flex flex-col w-full shrink-0 rounded-lg bg-bg-card border border-border-default p-3 shadow-[0_10px_40px_-5px_rgba(0,0,0,0.5),0_0_0_2px_rgba(74,124,111,0.15)]">
      {/* Header with icon + title + close button */}
      <div className="flex items-center gap-2 mb-2">
        <div className="text-secondary">
          {getToolIcon()}
        </div>
        <div className="text-dls-body font-medium text-primary flex-1">
          {title}
        </div>
        <div className="absolute top-spacing-2 right-spacing-2">
          <IconButton
            variant="ghost"
            onClick={onDeny}
            aria-label="Close modal"
          >
            <CrossIcon />
          </IconButton>
        </div>
      </div>

      {/* Command preview */}
      <div className="rounded-md py-2 px-2.5 bg-bg-page mb-3 max-h-[200px] overflow-y-auto">
        <div className="text-secondary font-mono leading-[18px] break-all whitespace-pre-wrap">
          {command}
        </div>
      </div>

      {/* Vertical option list + footer hint on same row as last option */}
      <div className="flex flex-col gap-0.5">
        {options.map((option, index) => (
          <button
            key={option.key}
            ref={el => buttonRefs.current[index] = el}
            onClick={actions[index]}
            onMouseEnter={() => setFocusedIndex(index)}
            className={`flex items-center gap-2.5 w-full px-[8px] pt-0 py-[4px] transition-colors focus:outline-none border-l-2 ${focusedIndex === index
              ? 'border-l-accent bg-bg-hover text-primary'
              : 'border-l-transparent text-secondary hover:bg-bg-hover'
              }`}
          >
            <kbd className="inline-flex items-center justify-center px-[6px] py-[4px] bg-bg-well border border-border-default rounded text-[12px] font-mono text-primary min-w-[20px]">{option.key}</kbd>
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );
});

ToolApprovalPopup.displayName = 'ToolApprovalPopup';

ToolApprovalPopup.propTypes = {
  /** Tool name (e.g., 'bash', 'edit_file', 'create_file') */
  tool: PropTypes.string.isRequired,
  /** Human-readable title (e.g., 'Run: npm run build') */
  title: PropTypes.string.isRequired,
  /** Command/input to display (e.g., the full bash command) */
  command: PropTypes.string.isRequired,
  /** Callback when user approves */
  onAllow: PropTypes.func.isRequired,
  /** Callback when user approves all future uses of this tool type */
  onAlwaysAllow: PropTypes.func.isRequired,
  /** Callback when user denies */
  onDeny: PropTypes.func.isRequired,
};

export default ToolApprovalPopup;
